import { lazy, ComponentType } from 'react';

/**
 * A wrapper around React.lazy that retries the import if it fails.
 * This is useful for handling network glitches or temporary unavailability of chunks.
 * Also handles "Failed to fetch dynamically imported module" errors by reloading the page once,
 * which typically happens after a new deployment when old chunks are no longer available.
 * 
 * @param factory The dynamic import function (e.g. () => import('./Component'))
 * @param retries Number of retry attempts (default: 2)
 * @returns A React.lazy component
 */
export const lazyWithRetry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2
) =>
  lazy(() =>
    new Promise<{ default: T }>((resolve, reject) => {
      const attempt = (left: number) => {
        factory()
          .then(resolve)
          .catch((error) => {
            // Check if the error is a chunk load error (deployment update)
            const isChunkLoadError = 
              error?.message?.includes('Failed to fetch dynamically imported module') ||
              error?.message?.includes('Importing a module script failed');

            if (isChunkLoadError) {
              // Prevent infinite reload loops using sessionStorage
              const storageKey = `retry-reload-${window.location.pathname}`;
              const hasReloaded = sessionStorage.getItem(storageKey);

              if (!hasReloaded) {
                console.log('Chunk load failed, reloading page to get new version...');
                sessionStorage.setItem(storageKey, 'true');
                window.location.reload();
                return;
              }
            }

            if (left === 0) {
              reject(error);
              return;
            }
            
            // Retry after a small delay
            setTimeout(() => attempt(left - 1), 1500);
          });
      };
      attempt(retries);
    })
  );
